import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { X, Users, Trophy, HeartCrack, Factory, DollarSign, Scissors } from 'lucide-react';
import { HireEmployeesWizard } from './HireEmployeesWizard';
import { WinCustomerWizard } from './WinCustomerWizard';
import { LoseCustomerWizard } from './LoseCustomerWizard';
import { CapitalPurchaseWizard } from './CapitalPurchaseWizard';
import { SecureFinancingWizard } from './SecureFinancingWizard';
import { CostReductionWizard } from './CostReductionWizard';

interface BusinessEventsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectEvent: (eventType: string, eventData?: any) => void;
}

const businessEvents = [
  {
    id: 'hire-employees',
    icon: '👥',
    title: 'Hire Employees',
    description: 'Model salary, NI, recruitment, equipment, office space, and potential revenue impact',
  },
  {
    id: 'win-customer',
    icon: '🏆',
    title: 'Win New Customer',
    description: 'Model new revenue stream with payment terms, setup costs, and ongoing expenses',
  },
  {
    id: 'lose-customer',
    icon: '💔',
    title: 'Lose Existing Customer',
    description: 'Model revenue loss and cost reductions',
  },
  {
    id: 'capital-purchase',
    icon: '🏭',
    title: 'Capital Purchase',
    description: 'Model equipment/property purchase with financing options and depreciation',
  },
  {
    id: 'secure-financing',
    icon: '💰',
    title: 'Secure Financing',
    description: 'Model loan, overdraft, or equity raise with repayment schedule',
  },
  {
    id: 'cost-reduction',
    icon: '✂️',
    title: 'Cost Reduction Program',
    description: 'Model cost cuts across categories with implementation timeline',
  },
];

export const BusinessEventsModal: React.FC<BusinessEventsModalProps> = ({
  open,
  onOpenChange,
  onSelectEvent,
}) => {
  const [showHireWizard, setShowHireWizard] = useState(false);
  const [showWinCustomerWizard, setShowWinCustomerWizard] = useState(false);
  const [showLoseCustomerWizard, setShowLoseCustomerWizard] = useState(false);
  const [showCapitalPurchaseWizard, setShowCapitalPurchaseWizard] = useState(false);
  const [showSecureFinancingWizard, setShowSecureFinancingWizard] = useState(false);
  const [showCostReductionWizard, setShowCostReductionWizard] = useState(false);

  const handleEventClick = (eventId: string) => {
    onOpenChange(false);
    
    switch (eventId) {
      case 'hire-employees':
        setShowHireWizard(true);
        break;
      case 'win-customer':
        setShowWinCustomerWizard(true);
        break;
      case 'lose-customer':
        setShowLoseCustomerWizard(true);
        break;
      case 'capital-purchase':
        setShowCapitalPurchaseWizard(true);
        break;
      case 'secure-financing':
        setShowSecureFinancingWizard(true);
        break;
      case 'cost-reduction':
        setShowCostReductionWizard(true);
        break;
      default:
        onSelectEvent(eventId);
    }
  };

  const handleWizardComplete = (eventType: string) => (eventData: any) => {
    onSelectEvent(eventType, eventData);
    // Close all wizards
    setShowHireWizard(false);
    setShowWinCustomerWizard(false);
    setShowLoseCustomerWizard(false);
    setShowCapitalPurchaseWizard(false);
    setShowSecureFinancingWizard(false);
    setShowCostReductionWizard(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Business Event</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Choose an event type to model its full impact on your cash flow:
          </p>

          <div className="space-y-3">
            {businessEvents.map((event) => (
              <div
                key={event.id}
                className="border rounded-lg p-4 hover:border-primary hover:bg-accent cursor-pointer transition-colors"
                onClick={() => handleEventClick(event.id)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{event.icon}</span>
                      <h3 className="font-semibold">{event.title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {event.description}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm">
                    Select →
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <HireEmployeesWizard
      open={showHireWizard}
      onOpenChange={setShowHireWizard}
      onComplete={handleWizardComplete('hire-employees')}
    />
    
    <WinCustomerWizard
      open={showWinCustomerWizard}
      onOpenChange={setShowWinCustomerWizard}
      onComplete={handleWizardComplete('win-customer')}
    />
    
    <LoseCustomerWizard
      open={showLoseCustomerWizard}
      onOpenChange={setShowLoseCustomerWizard}
      onComplete={handleWizardComplete('lose-customer')}
    />
    
    <CapitalPurchaseWizard
      open={showCapitalPurchaseWizard}
      onOpenChange={setShowCapitalPurchaseWizard}
      onComplete={handleWizardComplete('capital-purchase')}
    />
    
    <SecureFinancingWizard
      open={showSecureFinancingWizard}
      onOpenChange={setShowSecureFinancingWizard}
      onComplete={handleWizardComplete('secure-financing')}
    />
    
    <CostReductionWizard
      open={showCostReductionWizard}
      onOpenChange={setShowCostReductionWizard}
      onComplete={handleWizardComplete('cost-reduction')}
    />
    </>
  );
};
